import { Injectable } from '@angular/core';

import { Action, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

import { ApplicationConfigService, isNotUndefined, Toast } from '@stylehaven/components';
import { SetSelectedItemIds } from '../../../../state/search-options/search-options.actions';
import { SearchOptions } from '../../../../state/search-options/search-options.entity';
import { getSearchOptions } from '../../../../state/search-options/search-options.selector';
import { SharedApplicationState } from '../../../../state/shared-state.interface';
import { AddToast, ClearToasts, DeleteToast, UpdateToast, UpsertToast } from '../../../../state/toast/toast.actions';
import { selectAllToasts } from '../../../../state/toast/toast.selector';
import { SetFiltersPanelState, SetRecordTreePanelState } from '../../../../state/view-options/view-options.actions';
import { Overview, ViewOptions } from '../../../../state/view-options/view-options.entity';
import { getViewOptions } from '../../../../state/view-options/view-options.selector';
import { UserStoreService } from '../user/user-store.service';

@Injectable({ providedIn: 'root' })
export class StoreService {
    constructor(
        protected store: Store<SharedApplicationState>,
        protected userStoreService: UserStoreService,
        protected configService: ApplicationConfigService
    ) {}

    public dispatch(action: Action): void {
        this.store.dispatch(action);
    }

    // #region Search data
    public getSearchOptions(view: Overview): Observable<SearchOptions> {
        return this.store.select(getSearchOptions(view)).pipe(isNotUndefined());
    }

    public getSelectedItemIds(view: Overview): Observable<string[]> {
        return this.getSearchOptions(view).pipe(
            map(searchOptions => searchOptions.selectedItemIds ?? []),
            startWith([])
        );
    }

    public saveSelectedItemIds(view: Overview, itemIds: string[]): void {
        return this.dispatch(new SetSelectedItemIds(view, itemIds));
    }
    // #endregion

    // #region View data
    public getViewOptions(view: Overview): Observable<ViewOptions> {
        return this.store.select(getViewOptions(view)).pipe(isNotUndefined());
    }

    public getFiltersPanelState(view: Overview): Observable<boolean> {
        return this.getViewOptions(view).pipe(
            map((viewOptions: ViewOptions) => viewOptions.filtersPanelOpen ?? true),
            isNotUndefined(),
            startWith(true)
        );
    }

    public getRecordTreePanelState(view: Overview): Observable<boolean> {
        return this.getViewOptions(view).pipe(
            map((viewOptions: ViewOptions) => viewOptions.recordTreePanelOpen ?? true),
            isNotUndefined(),
            startWith(true)
        );
    }

    public saveFiltersPanelState(view: Overview, opened: boolean): void {
        this.dispatch(new SetFiltersPanelState(view, opened));
    }

    public saveRecordTreePanelState(view: Overview, opened: boolean): void {
        this.dispatch(new SetRecordTreePanelState(view, opened));
    }
    // #endregion

    // #region Toasts
    public getToasts(): Observable<Toast[]> {
        return this.store.select(selectAllToasts);
    }

    public clearToasts(): void {
        this.dispatch(new ClearToasts());
    }

    public addNewToast(toast: Toast): void {
        this.dispatch(new AddToast(toast));
    }

    public addToast(toast: Toast): void {
        this.dispatch(new UpsertToast(toast));
    }

    public deleteToast(id: number): void {
        this.dispatch(new DeleteToast(id));
    }

    public updateToast(id: number, toast: Partial<Toast>): void {
        this.dispatch(new UpdateToast(id, toast));
    }
    // #endregion
}
